/**
 * 검색 색인 만들기 (M7). 잠금 해제된 동안 메모리에만 두고, 로그아웃하면 Repository와 함께 사라진다.
 * 민감정보(실명·연락처·주소)는 색인에 넣지 않는다.
 */
import type { Session } from '@/domain/schema/v1';
import { SearchIndex, type SearchDoc } from '@/domain/search/search';
import { JOURNAL_FIELD_LABELS } from '@/domain/workflow/journal';
import { FORMULATION_ITEM_FIELDS, FORMULATION_LABELS, FORMULATION_LIST_FIELDS } from '@/domain/workflow/formulation';
import type { Repository } from '@/data/repo/repository';

const mmss = (ms: number) => {
  const s = Math.floor(ms / 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return s >= 3600
    ? `${Math.floor(s / 3600)}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`
    : `${p(Math.floor(s / 60))}:${p(s % 60)}`;
};
const S = (n: number) => `S${String(n).padStart(2, '0')}`;
const join = (xs: (string | undefined | null)[]) => xs.filter(Boolean).join('\n');

const cache = new WeakMap<Repository, { index: SearchIndex; builtAt: number }>();
const MAX_AGE_MS = 2 * 60 * 1000;

export async function getSearchIndex(repo: Repository, opts: { refresh?: boolean } = {}): Promise<SearchIndex> {
  const c = cache.get(repo);
  if (c && !opts.refresh && Date.now() - c.builtAt < MAX_AGE_MS) return c.index;
  const index = new SearchIndex(await buildDocs(repo));
  cache.set(repo, { index, builtAt: Date.now() });
  return index;
}

export async function buildDocs(repo: Repository): Promise<SearchDoc[]> {
  const [clients, sessions] = await Promise.all([repo.list('Client'), repo.list('Session')]);
  const code = new Map(clients.map((c) => [c.id, c.clientCode]));
  const sess = new Map<string, Session>(sessions.filter((s) => code.has(s.clientId)).map((s) => [s.id, s]));
  const docs: SearchDoc[] = [];
  const base = (clientId: string, sessionId?: string) => {
    const s = sessionId ? sess.get(sessionId) : undefined;
    return {
      clientId,
      clientCode: code.get(clientId) ?? '?',
      ...(s ? { sessionId: s.id, sessionNumber: s.sessionNumber, date: s.sessionDate } : {}),
    };
  };

  // 내담자 기본 정보·초기 상담 요약 (민감정보 제외)
  const intakes = await repo.list('IntakeSummary');
  for (const c of clients) {
    const intake = intakes.find((x) => x.clientId === c.id);
    docs.push({
      id: `client-${c.id}`,
      kind: 'client',
      ...base(c.id),
      source: `${c.clientCode} · 내담자 정보`,
      text: join([
        c.clientCode,
        c.alias,
        c.occupationRole,
        c.education,
        c.religion,
        c.referralSource,
        c.priorCounselingHistory,
        c.medicalPsychiatricHistory,
        c.familyStructure,
        c.faithSpiritualContext,
        ...(c.importantRelationships ?? []),
        ...(c.majorLifeEvents ?? []),
        ...(c.tags ?? []),
        ...(intake?.presentingProblems ?? []),
        intake?.referralReason,
        intake?.functionalImpact,
        ...(intake?.strengths ?? []),
      ]),
      link: `/clients/${c.id}/profile`,
    });
  }

  // 전사 (Working Transcript, 맞장구 제외) — 줄 단위, 누르면 그 줄로
  for (const s of sess.values()) {
    if (!s.workingTranscriptId) continue;
    const w = await repo.find('Transcript', s.workingTranscriptId);
    for (const seg of w?.segments ?? []) {
      if (seg.isBackchannel || !seg.text.trim()) continue;
      const who = seg.clinicalRole === 'counselor' ? '상담자' : seg.clinicalRole === 'client' ? '내담자' : '화자 불명';
      docs.push({
        id: `tr-${s.id}-${seg.segmentId}`,
        kind: 'transcript',
        ...base(s.clientId, s.id),
        source: `${S(s.sessionNumber)} · ${mmss(seg.startMs ?? 0)} · 전사 (${who})`,
        text: seg.text,
        link: `/sessions/${s.id}?seg=${encodeURIComponent(seg.segmentId)}`,
      });
    }
  }

  const [memos, journals, analyses, evidence, revisions, goals, progress, diagnoses, briefs, documents] =
    await Promise.all([
      repo.list('CounselorMemo'),
      repo.list('CounselingJournal'),
      repo.list('SessionAnalysis'),
      repo.list('ClinicalEvidence'),
      repo.list('CaseFormulationRevision'),
      repo.list('CounselingGoal'),
      repo.list('GoalProgress'),
      repo.list('DiagnosticConsideration'),
      repo.list('NextSessionBrief'),
      repo.list('DocumentArtifact'),
    ]);
  const live = (x: { clientId: string; sessionId?: string }) =>
    code.has(x.clientId) && (!x.sessionId || sess.has(x.sessionId));

  for (const m of memos.filter(live))
    docs.push({
      id: `memo-${m.id}`,
      kind: 'memo',
      ...base(m.clientId, m.sessionId),
      source: `${S(sess.get(m.sessionId)!.sessionNumber)} · 상담자 메모`,
      text: m.text,
      link: `/sessions/${m.sessionId}`,
    });

  for (const j of journals.filter((x) => live(x) && !x.supersededById)) {
    const n = sess.get(j.sessionId)!.sessionNumber;
    const text = join(
      (Object.keys(JOURNAL_FIELD_LABELS) as (keyof typeof JOURNAL_FIELD_LABELS)[]).map((k) =>
        typeof j[k] === 'string' && j[k] ? `${JOURNAL_FIELD_LABELS[k]}: ${j[k] as string}` : null,
      ),
    );
    docs.push({
      id: `journal-${j.id}`,
      kind: 'journal',
      ...base(j.clientId, j.sessionId),
      source: `${S(n)} · 상담기록지${j.status === 'approved' ? '' : ' (초안)'}`,
      text,
      link: `/sessions/${j.sessionId}/journal`,
    });
  }

  for (const a of analyses.filter(live)) {
    const n = sess.get(a.sessionId)!.sessionNumber;
    const text = join([
      a.sessionSummary,
      ...(a.keyEvents ?? []),
      ...(a.notableChanges ?? []),
      ...[
        ...(a.emotions ?? []),
        ...(a.cognitions ?? []),
        ...(a.behaviors ?? []),
        ...(a.relationshipPatterns ?? []),
      ].map((x) => x.text),
      ...(a.interventions ?? []).map((x) => join([x.interventionName, x.theoreticalOrientation, x.clientResponse])),
    ]);
    docs.push({
      id: `analysis-${a.id}`,
      kind: 'analysis',
      ...base(a.clientId, a.sessionId),
      source: `${S(n)} · 회기 분석`,
      text,
      link: `/sessions/${a.sessionId}`,
    });
  }

  const EV: Record<string, string> = {
    FACT: '사실',
    CLIENT_REPORT: '내담자 진술',
    COUNSELOR_OBSERVATION: '상담자 관찰',
    HYPOTHESIS: '가설',
  };
  for (const e of evidence.filter(live)) {
    const s = e.sessionId ? sess.get(e.sessionId) : undefined;
    docs.push({
      id: `ev-${e.id}`,
      kind: 'evidence',
      ...base(e.clientId, e.sessionId),
      source: `${s ? `${S(s.sessionNumber)} · ` : ''}${EV[e.evidenceType] ?? '근거'}${e.needsVerification ? ' (확인 필요)' : ''}`,
      text: e.text,
      link: s ? `/sessions/${s.id}` : `/clients/${e.clientId}`,
    });
  }

  for (const f of revisions.filter(live)) {
    const text = join([
      ...FORMULATION_LIST_FIELDS.flatMap((k) => (f[k] ?? []).map((x) => `${FORMULATION_LABELS[k]}: ${x}`)),
      ...FORMULATION_ITEM_FIELDS.flatMap((k) => (f[k] ?? []).map((x) => `${FORMULATION_LABELS[k]}: ${x.text}`)),
      ...(f.theoryFormulations ?? []).map((t) => `${t.orientation}: ${t.formulation}`),
      f.changeReason,
    ]);
    docs.push({
      id: `cf-${f.id}`,
      kind: 'formulation',
      ...base(f.clientId),
      date: f.createdAt.slice(0, 10),
      source: `${code.get(f.clientId)} · 사례개념화 v${f.version}${f.status === 'proposal' ? ' (개정안)' : ''}`,
      text,
      link: `/clients/${f.clientId}/formulation`,
    });
  }

  for (const g of goals.filter(live)) {
    const notes = progress.filter((p) => p.goalId === g.id && sess.has(p.sessionId));
    const text = join([
      g.title,
      g.description,
      g.targetBehavior,
      g.measurementOrIndicator,
      ...notes.map((p) => join([p.progressNote, p.clientAssessment, p.counselorAssessment])),
    ]);
    docs.push({
      id: `goal-${g.id}`,
      kind: 'goal',
      ...base(g.clientId),
      source: `${code.get(g.clientId)} · 상담 목표`,
      text,
      link: `/clients/${g.clientId}/goals`,
    });
  }

  for (const d of diagnoses.filter(live)) {
    const text = join([
      ...d.considerations.map((c) =>
        join([c.diagnosisName, ...(c.criteriaToAssess ?? []), ...(c.differentialConsiderations ?? [])]),
      ),
      ...(d.symptomList ?? []),
    ]);
    docs.push({
      id: `dx-${d.id}`,
      kind: 'diagnosis',
      ...base(d.clientId),
      date: d.createdAt.slice(0, 10),
      source: `${code.get(d.clientId)} · 진단적 고려 v${d.version}`,
      text,
      link: `/clients/${d.clientId}/diagnosis`,
    });
  }

  for (const b of briefs.filter(live)) {
    const src = sess.get(b.sourceSessionId);
    const text = join([
      b.previousSessionSummary,
      ...(b.sessionObjectives ?? []),
      ...(b.hypothesesToTest ?? []),
      ...(b.assessmentQuestions ?? []).map((q) => q.question),
      ...(b.interventionSuggestions ?? []).map((x) => x.intervention),
      ...(b.riskFollowUp ?? []),
      ...(b.unfinishedTopics ?? []),
    ]);
    docs.push({
      id: `brief-${b.id}`,
      kind: 'brief',
      ...base(b.clientId),
      ...(src ? { date: src.sessionDate } : {}),
      source: `${code.get(b.clientId)} · ${b.targetSessionNumber}회기 준비`,
      text,
      link: `/clients/${b.clientId}/brief`,
    });
  }

  const DOC: Record<string, string> = {
    session_record: '상담기록지 문서',
    case_report: '사례보고서',
    termination_report: '종결보고서',
    verbatim: '축어록',
    dialogue: '대화록',
  };
  for (const d of documents.filter(live))
    docs.push({
      id: `doc-${d.id}`,
      kind: 'document',
      ...base(d.clientId),
      date: d.asOf.slice(0, 10),
      source: `${code.get(d.clientId)} · ${DOC[d.documentType] ?? '문서'} (${d.asOf.slice(0, 10)})`,
      text: d.content.replace(/\{\{이름\}\}/g, ''),
      link: `/clients/${d.clientId}/documents?doc=${d.id}`,
    });

  return docs;
}
